import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import HiResAudioBadge from '../HiResAudioBadge';
import { isBadCoverUrl, isCoverProxyUrl, withCoverThumbParam, isCoverThumbRequest, withoutCoverThumbParam, logCoverThumb404, normalizeArtworkCacheKey, pickCoverArtThumbHash } from '../../utils/coverArt';
import {
  getCoverArtBatch,
  getCoverFromCache,
  getRemoteCoverArt,
  getRemoteCoverFromCache,
  hasResolvedCoverInCache,
  isCoverIPCAvailable,
  isRemoteCoverViewerShell,
  normalizeCoverHash,
  pickAlbumFolderPath,
  resolveLocalAlbumCoverDisplay,
  subscribeCoverCacheLoaded,
} from '../../lib/coverArtIPC';
import {
  getExternalDetailHash,
  isExternalCoverKnownArtless,
  requestExternalAlbumCoverHash,
  subscribeExternalCoverResolve,
} from '../../lib/externalAlbumCoverResolve';
import { shouldUseLocalLibraryIpc } from '../../services/localLibraryIpcGate';
import { isIpcPostMessageFallbackActive } from '../../lib/connector';
import { useShallow } from 'zustand/react/shallow';
import { useCollectorMode } from '../../store/libraryUIStore';
import { useMaestroStore } from '../../store/maestroStore';
import { useAlbumCoverOverlay, shouldShowAlbumUpdatingBadge, subscribeAlbumUpdatingBadges } from '../../hooks/useLibraryImageEnrichment';
import {
  getFormatBadge,
  getSourceBadge,
  isMissingArtwork,
  isDsd,
  isHiRes,
} from '../../utils/formatDetection';
import { isScanActive } from '../../services/libraryScanCoordinator';
import { logReactBoundaryError } from '../../utils/diagnosticUncaught';
import { dedupeAlbumIndexRows, getAlbumGridRowKey } from '../../services/libraryAlbumIndex';
import {
  isProvisionalAlbum,
  notifyProvisionalCatalogActionBlocked,
} from '../../services/catalogBootSnapshot';
import { getBackendVersionCount } from '../../utils/versionGrouping';

/**
 * Cover tile render during progressive art load (A/B testing):
 * - 'tile-appears-with-art': omit the album tile until its cover URL resolves (progressive grid fill)
 * - 'tile-present-art-blank': tile shell stays; art area empty until real cover (default)
 */
export const COVER_TILE_RENDER_MODE = 'tile-present-art-blank';
import {
  mayUseEnrichedCoverUrl,
  pickLocalLibraryDisplayCover,
  shouldDeferEnrichedCoverForLocalAlbum,
} from '../../utils/localLibraryCoverPrecedence';
import AlbumTileOverflowMenu from './AlbumTileOverflowMenu';
import AlbumPlayButton from '../AlbumPlayButton';
import { playAlbumFromTile } from '../../utils/playAlbumFromTile';
import { albumMatchesNowPlaying, usePlaybackActive } from '../../hooks/useAlbumTilePlayback';
import {
  fetchValidatedLocalThumbUrl,
  invalidateTileCoverArtOnLoadError,
  pickRemoteTileCoverUrl,
  syncLocalThumbDisplayUrl,
} from '../../utils/tileCoverArt';
import {
  beginViewportFirstResolutionWaveTiming,
  bumpViewportCoverGeneration,
  getViewportCoverPrimeGeneration,
  primeViewportCoverResolution,
  subscribeViewportCoverPrime,
} from '../../lib/viewportCoverQueue';
import {
  ALBUM_GRID_DEFAULT_ROW_HEIGHT,
  ALBUM_GRID_MIN_COLUMN,
  BROWSE_GRID_GAP,
  BROWSE_INITIAL_VISIBLE_ROWS,
  BROWSE_OVERSCAN_ROWS_BOTTOM,
  BROWSE_OVERSCAN_ROWS_TOP,
  computeBrowseWindowRange,
  expectedMountedAlbumGridCards,
  getBrowseScrollParent,
  measureBrowseColumnCount,
} from '../../utils/browseGridWindowing';

export { expectedMountedAlbumGridCards };

// Zustand v5's `useStore(selector, equalityFn)` two-arg form was REMOVED — the
// equality function is silently dropped and React.useSyncExternalStore falls
// back to Object.is. Returning a fresh object literal from the selector then
// re-fires every commit, producing the "getSnapshot should be cached"
// warning followed by React #185 ("Maximum update depth exceeded"). The
// canonical fix is `useShallow`, which caches the prior reference and returns
// it when the new selector result shallow-equals it (same pattern as the
// be870c72 HiddenAlbums fix, just expressed via the shallow helper instead
// of an explicit useMemo because here we're synthesising a new object from
// multiple store fields rather than projecting a stored container).
function useNowPlayingAlbumKey() {
  return useMaestroStore(
    useShallow((s) => {
      const t = s.currentTrack;
      if (!t || s.playbackState === 'stopped') return null;
      return {
        albumId: String(t.albumId || t.album_id || t.serviceAlbumId || ''),
        albumTitle: (t.album || t.albumTitle || '').toLowerCase().trim(),
        artistName: (t.artist || t.artistName || '').toLowerCase().trim(),
      };
    }),
  );
}

function isNowPlaying(album, npKey) {
  return albumMatchesNowPlaying(album, npKey);
}

export function hashColor(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  const h = Math.abs(hash) % 360;
  return `hsl(${h}, 35%, 25%)`;
}

function syncResolvedCoverUrl(embHash, folHash, extHash) {
  for (const h of [embHash, folHash, extHash]) {
    if (hasResolvedCoverInCache(h)) return getCoverFromCache(h) || null;
  }
  return null;
}

/** Grid wall tile — delegates to the shared local cover display resolver. */
async function resolveGridHashCoverInOrder(primaryEmbHash, folHash, extHash, album) {
  return resolveLocalAlbumCoverDisplay({
    primaryEmbeddedHash: primaryEmbHash,
    folderHash: folHash,
    externalHash: extHash,
    album,
  });
}

export function placeholderDataUri(title) {
  const color = hashColor(title);
  const safe = String(title || 'Untitled').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const lines = safe.length > 30 ? [safe.slice(0, 20), safe.slice(20, 40)] : [safe];
  const textY = lines.length > 1 ? 185 : 200;
  const textSvg = lines.map((l, i) =>
    `<text x="200" y="${textY + i * 36}" font-family="system-ui,sans-serif" font-size="28" font-weight="800" fill="rgba(255,255,255,0.85)" text-anchor="middle">${l}</text>`
  ).join('');
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400"><rect width="400" height="400" fill="${color}"/>${textSvg}</svg>`;
  return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
}

const GRID_GAP = BROWSE_GRID_GAP;
const DEFAULT_ROW_HEIGHT = ALBUM_GRID_DEFAULT_ROW_HEIGHT;

/** Coerce album metadata to a safe display string — never pass objects to JSX. */
function coerceDisplayText(value, fallback = '') {
  if (value == null || value === '') return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object') {
    for (const key of ['en', 'text', 'label', 'name', 'title', 'value']) {
      const nested = value[key];
      if (typeof nested === 'string') return nested;
    }
    return fallback;
  }
  return String(value);
}

/** Cover URLs must be plain strings — reject object-shaped API payloads. */
function coerceCoverUrl(value) {
  if (value == null) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    for (const key of ['url', 'href', 'src', 'en']) {
      const nested = value[key];
      if (typeof nested === 'string' && nested) return nested;
    }
    return null;
  }
  const s = String(value);
  return s.startsWith('[object ') ? null : s;
}

class AlbumCardErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    console.warn('[AlbumCard] render error — showing placeholder:', error?.message);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return <AlbumCardPlaceholder />;
    }
    return this.props.children;
  }
}

class AlbumGridErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error('[AlbumGrid] render error:', error?.message);
    logReactBoundaryError(error, info);
  }

  componentDidUpdate(prevProps) {
    const prevKeys = prevProps.resetKeys;
    const nextKeys = this.props.resetKeys;
    const keysChanged = Array.isArray(prevKeys) && Array.isArray(nextKeys)
      && (prevKeys.length !== nextKeys.length || prevKeys.some((k, i) => k !== nextKeys[i]));
    if (keysChanged && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="px-4 py-12 text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
          Album grid paused — scroll or wait for more albums to load.
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * AlbumGrid — Phase 1 mission-control album wall.
 *
 * Behavior:
 *   - Single click  → onAlbumClick (highlight / open)
 *   - Double click  → onAlbumDoubleClick (PLAY via QueueController upstream)
 *   - Right-click   → onAlbumContextMenu (caller decides — see LibraryBrowser)
 *
 * Visual contract:
 *   - Calm by default. Only the format-quality badge sits on the cover.
 *   - On hover, the source badge (Local/NAS/Qobuz) and any warnings
 *     (missing artwork, duplicate) fade in over 150ms.
 *   - Collector Mode (per-user persisted toggle) makes hover badges
 *     ALWAYS visible — no extra logic required, just a body-class flip.
 *   - Disconnected sources fade the tile (existing behavior preserved).
 */
export default function AlbumGrid({
  albums,
  totalItemCount,
  onAlbumClick,
  onAlbumEdit,
  onAlbumDoubleClick,
  onAlbumContextMenu,
  onAlbumInspect,
  onAlbumReread,
  onAlbumRemove,
  homeLibraryOffline,
  hasMore,
  loadingMore,
  onLoadMore,
  loadMoreRearmToken = 0,
  restoredScrollTop = null,
  restoredWindowRange = null,
  onScrollRestored,
  scrollElementRef = null,
  scrollSnapshotRef = null,
  selectedAlbumKey,
  disconnectedSourceIds,
  reconnectingSourceIds,
  onAlbumVersionsClick,
  jumpToIndex = null,
  jumpSeq = 0,
  /** Boot snapshot — global scan envelope (header banner only; not per-tile). */
  catalogUpdating = false,
}) {
  const [, bumpUpdatingBadges] = useState(0);
  useEffect(() => subscribeAlbumUpdatingBadges(() => {
    bumpUpdatingBadges((n) => n + 1);
  }), []);

  const list = useMemo(() => {
    const rows = (Array.isArray(albums) ? albums : []).filter((a) => (a.track_count ?? 1) > 0);
    return dedupeAlbumIndexRows(rows).albums;
  }, [albums]);
  const virtualItemCount = Math.max(
    list.length,
    Number.isFinite(totalItemCount) && totalItemCount > 0 ? totalItemCount : list.length,
  );
  const npKey = useNowPlayingAlbumKey();
  const playbackActive = usePlaybackActive();
  const rootRef = useRef(null);
  const gridRef = useRef(null);
  const scrollParentRef = useRef(null);
  const rowHeightRef = useRef(DEFAULT_ROW_HEIGHT);
  const rafRef = useRef(null);
  const prevStartRowRef = useRef(-1);
  const sentinelRef = useRef(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  const [layoutMetrics, setLayoutMetrics] = useState({
    columns: 4,
    rowHeight: DEFAULT_ROW_HEIGHT,
  });
  const [windowRange, setWindowRange] = useState({
    startRow: 0,
    endRow: BROWSE_INITIAL_VISIBLE_ROWS + BROWSE_OVERSCAN_ROWS_BOTTOM,
  });
  const columnCountRef = useRef(4);

  const updateVisibleWindow = useCallback(() => {
    const scrollEl = scrollParentRef.current;
    if (!scrollEl) return;

    const rowHeight = rowHeightRef.current;
    const columns = columnCountRef.current;
    const scrollTop = scrollEl.scrollTop;
    const viewportHeight = scrollEl.clientHeight;

    const range = computeBrowseWindowRange({
      itemCount: virtualItemCount,
      columns,
      rowHeight,
      scrollTop,
      viewportHeight,
    });

    setWindowRange((prev) => {
      if (prev.startRow !== range.startRow && prevStartRowRef.current >= 0 && range.startRow !== prevStartRowRef.current) {
        bumpViewportCoverGeneration();
      }
      prevStartRowRef.current = range.startRow;
      return prev.startRow === range.startRow && prev.endRow === range.endRow
        ? prev
        : { startRow: range.startRow, endRow: range.endRow };
    });

    if (scrollSnapshotRef) {
      scrollSnapshotRef.current = {
        scrollTop: scrollEl.scrollTop,
        startRow: range.startRow,
        endRow: range.endRow,
        itemCount: virtualItemCount,
        loadedCount: list.length,
      };
    }

    if (hasMore && range.endIndex >= list.length - columns) {
      onLoadMoreRef.current?.();
    }
  }, [list.length, virtualItemCount, hasMore, scrollSnapshotRef]);

  const measureLayout = useCallback(() => {
    const grid = gridRef.current || rootRef.current;
    if (!grid) return;
    const columns = measureBrowseColumnCount(grid.clientWidth, ALBUM_GRID_MIN_COLUMN);
    columnCountRef.current = columns;
    setLayoutMetrics((prev) => (
      prev.columns === columns ? prev : { ...prev, columns }
    ));
    updateVisibleWindow();
  }, [updateVisibleWindow]);

  const prevAlbumsRef = useRef(albums);
  useEffect(() => {
    if (prevAlbumsRef.current === albums) return;
    prevAlbumsRef.current = albums;
    updateVisibleWindow();
  }, [albums, updateVisibleWindow]);

  const scrollRestoredRef = useRef(false);
  useEffect(() => {
    if (restoredScrollTop == null || restoredScrollTop <= 0 || scrollRestoredRef.current) return;

    const applyScroll = () => {
      const scrollEl = scrollParentRef.current;
      if (!scrollEl) return false;
      if (restoredWindowRange) {
        setWindowRange(restoredWindowRange);
      }
      const maxScroll = scrollEl.scrollHeight - scrollEl.clientHeight;
      scrollEl.scrollTop = Math.min(restoredScrollTop, Math.max(0, maxScroll));
      updateVisibleWindow();
      return true;
    };

    const finishRestore = () => {
      if (scrollRestoredRef.current) return;
      scrollRestoredRef.current = true;
      onScrollRestored?.();
    };

    let retryTimer = null;
    requestAnimationFrame(() => {
      applyScroll();
      requestAnimationFrame(() => {
        applyScroll();
        retryTimer = window.setTimeout(() => {
          applyScroll();
          finishRestore();
        }, 180);
      });
    });

    return () => {
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [restoredScrollTop, restoredWindowRange, updateVisibleWindow, onScrollRestored]);

  const bindScrollContainer = useCallback(() => {
    const explicit = scrollElementRef?.current;
    const scrollEl = explicit || getBrowseScrollParent(rootRef.current);
    scrollParentRef.current = scrollEl;
    return scrollEl;
  }, [scrollElementRef]);

  useEffect(() => {
    const scrollEl = bindScrollContainer();
    if (!scrollEl) return undefined;

    measureLayout();

    const onScroll = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(updateVisibleWindow);
    };
    const onResize = () => measureLayout();

    scrollEl.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);

    return () => {
      scrollEl.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [list.length, measureLayout, updateVisibleWindow, bindScrollContainer]);

  useEffect(() => {
    const scrollEl = bindScrollContainer();
    if (!scrollEl) return;
    updateVisibleWindow();
  }, [bindScrollContainer, updateVisibleWindow, layoutMetrics.columns]);

  const { startIndex, endIndex, topSpacer, bottomSpacer } = useMemo(() => {
    const columns = layoutMetrics.columns;
    const rowHeight = layoutMetrics.rowHeight;
    const totalRows = Math.max(1, Math.ceil(virtualItemCount / columns));
    const startRow = Math.min(windowRange.startRow, totalRows);
    const endRow = Math.min(totalRows, Math.max(windowRange.endRow, startRow + 1));
    return {
      startIndex: startRow * columns,
      endIndex: Math.min(virtualItemCount, endRow * columns),
      topSpacer: startRow * rowHeight,
      bottomSpacer: Math.max(0, (totalRows - endRow) * rowHeight),
    };
  }, [virtualItemCount, layoutMetrics, windowRange]);

  const visibleList = useMemo(
    () => list.slice(startIndex, Math.min(endIndex, list.length)),
    [list, startIndex, endIndex],
  );

  // Mount + visible-window settle: prime album tiles so demand is not scroll-only.
  useEffect(() => {
    if (visibleList.length === 0) return undefined;
    beginViewportFirstResolutionWaveTiming();
    const id = requestAnimationFrame(() => {
      primeViewportCoverResolution('album-visible-window');
    });
    return () => cancelAnimationFrame(id);
  }, [visibleList, startIndex, endIndex]);

  const cardStateRef = useRef(new Map());
  cardStateRef.current = new Map();
  visibleList.forEach((album, idx) => {
    const listIndex = startIndex + idx;
    const albumKey = getAlbumGridRowKey(album, listIndex);
    const isLocal = album?.__source === 'local_library' || album?.isLocalLibrary === true;
    const provisional = isProvisionalAlbum(album);
    cardStateRef.current.set(albumKey, {
      album,
      unavailable: (isLocal && homeLibraryOffline)
        || (album?.source_id && disconnectedSourceIds?.has?.(album.source_id)),
      provisional,
      isReconnecting: !!(album?.source_id && reconnectingSourceIds?.has?.(album.source_id)),
      versionCount: getBackendVersionCount(album),
      showUpdatingBadge: !!(provisional || shouldShowAlbumUpdatingBadge(album)),
    });
  });

  const handlersRef = useRef({});
  handlersRef.current = {
    onAlbumClick,
    onAlbumEdit,
    onAlbumDoubleClick,
    onAlbumContextMenu,
    onAlbumInspect,
    onAlbumReread,
    onAlbumRemove,
    onAlbumVersionsClick,
  };

  const clickTimerRef = useRef(null);
  useEffect(() => () => {
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
  }, []);

  const handleCardClick = useCallback((albumKey) => {
    const st = cardStateRef.current.get(albumKey);
    if (!st || st.unavailable) return;
    if (st.provisional) {
      notifyProvisionalCatalogActionBlocked();
      return;
    }
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null;
      handlersRef.current.onAlbumClick?.(st.album);
    }, 280);
  }, []);

  const handleCardDoubleClick = useCallback((albumKey) => {
    const st = cardStateRef.current.get(albumKey);
    if (!st || st.unavailable) return;
    if (st.provisional) {
      notifyProvisionalCatalogActionBlocked();
      return;
    }
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    handlersRef.current.onAlbumClick?.(st.album);
    handlersRef.current.onAlbumDoubleClick?.(st.album);
  }, []);

  const handleCardEdit = useCallback((albumKey) => {
    const st = cardStateRef.current.get(albumKey);
    if (!st || st.unavailable || st.provisional) {
      if (st?.provisional) notifyProvisionalCatalogActionBlocked();
      return;
    }
    handlersRef.current.onAlbumEdit?.(st.album);
  }, []);

  const handleCardContextMenu = useCallback((albumKey, event) => {
    const st = cardStateRef.current.get(albumKey);
    if (!st || st.unavailable) return;
    if (st.provisional) {
      event?.preventDefault?.();
      notifyProvisionalCatalogActionBlocked();
      return;
    }
    handlersRef.current.onAlbumContextMenu?.(event, st.album);
  }, []);

  const handleCardInspect = useCallback((albumKey) => {
    const st = cardStateRef.current.get(albumKey);
    if (!st || st.unavailable || st.provisional) {
      if (st?.provisional) notifyProvisionalCatalogActionBlocked();
      return;
    }
    handlersRef.current.onAlbumInspect?.(st.album);
  }, []);

  const handleCardReread = useCallback(async (albumKey) => {
    const st = cardStateRef.current.get(albumKey);
    if (!st || st.unavailable || st.provisional) {
      if (st?.provisional) notifyProvisionalCatalogActionBlocked();
      return;
    }
    await handlersRef.current.onAlbumReread?.(st.album);
  }, []);

  const handleCardRemove = useCallback(async (albumKey) => {
    const st = cardStateRef.current.get(albumKey);
    if (!st || st.unavailable || st.provisional) {
      if (st?.provisional) notifyProvisionalCatalogActionBlocked();
      return;
    }
    await handlersRef.current.onAlbumRemove?.(st.album);
  }, []);

  const handleCardVersions = useCallback((albumKey, event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    const st = cardStateRef.current.get(albumKey);
    if (!st || st.unavailable || st.provisional) {
      if (st?.provisional) notifyProvisionalCatalogActionBlocked();
      return;
    }
    handlersRef.current.onAlbumVersionsClick?.(st.album);
  }, []);

  const coverHashKey = useMemo(() => {
    const seen = new Set();
    const parts = [];
    for (const album of visibleList) {
      const emb = normalizeCoverHash(album?.embedded_cover_hash);
      const fol = normalizeCoverHash(album?.folder_cover_hash);
      for (const h of [emb, fol]) {
        if (h && !seen.has(h)) {
          seen.add(h);
          parts.push(h);
        }
      }
    }
    return parts.join('|');
  }, [visibleList]);

  useEffect(() => {
    const scrollEl = bindScrollContainer();
    if (!scrollEl) return;
    if (process.env.NODE_ENV !== 'production') {
      console.log('[album-grid] ready', {
        albumCount: list.length,
        scrollTarget: scrollElementRef?.current ? 'explicit-ref' : 'scroll-parent',
      });
    }
  }, [bindScrollContainer, scrollElementRef]);

  useEffect(() => {
    if (jumpToIndex == null || jumpToIndex < 0 || !Number.isFinite(jumpToIndex)) return;
    const scrollEl = scrollElementRef?.current || scrollParentRef.current || bindScrollContainer();
    if (!scrollEl) return;

    const columns = Math.max(1, columnCountRef.current || layoutMetrics.columns || 1);
    const rowHeight = Math.max(50, rowHeightRef.current || DEFAULT_ROW_HEIGHT);
    const row = Math.floor(jumpToIndex / columns);
    const targetTop = row * rowHeight;
    scrollEl.scrollTop = Math.max(0, targetTop - rowHeight * 0.35);

    requestAnimationFrame(() => {
      updateVisibleWindow();
    });
  }, [jumpToIndex, jumpSeq, list.length, scrollElementRef, bindScrollContainer, layoutMetrics.columns, updateVisibleWindow]);

  const isRemoteViewerShell = isRemoteCoverViewerShell();

  useEffect(() => {
    if (!coverHashKey) return;
    const hashes = coverHashKey.split('|').filter(Boolean).slice(0, 48);
    if (hashes.length === 0) return;
    let cancelled = false;
    const delayMs = isScanActive() ? 800 : 400;
    const timer = setTimeout(() => {
      if (cancelled) return;
      if (isRemoteViewerShell) {
        Promise.all(hashes.map((h) => getRemoteCoverArt(h, null)));
        return;
      }
      if (!isCoverIPCAvailable()) return;
      getCoverArtBatch(hashes);
    }, delayMs);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [coverHashKey, isRemoteViewerShell]);

  const handleIntersect = useCallback((entries) => {
    if (entries[0]?.isIntersecting) onLoadMoreRef.current?.();
  }, []);

  useEffect(() => {
    const el = sentinelRef.current;
    const scrollEl = scrollParentRef.current;
    if (!el || !hasMore) return;
    const io = new IntersectionObserver(handleIntersect, {
      root: scrollEl || null,
      rootMargin: '300px',
    });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, handleIntersect, bottomSpacer, list.length]);

  useEffect(() => {
    if (!loadMoreRearmToken || !hasMore) return;
    const el = sentinelRef.current;
    const scrollEl = scrollParentRef.current;
    if (!el) return;
    const rootBottom = scrollEl
      ? scrollEl.getBoundingClientRect().bottom + 300
      : window.innerHeight + 300;
    const sentinelTop = el.getBoundingClientRect().top;
    if (sentinelTop <= rootBottom) {
      onLoadMoreRef.current?.();
    }
  }, [loadMoreRearmToken, hasMore]);

  const handleMeasureCard = useCallback((node) => {
    if (!node) return;
    const height = node.getBoundingClientRect().height;
    if (height < 50 || Math.abs(height - rowHeightRef.current) <= 1) return;
    rowHeightRef.current = height;
    setLayoutMetrics((prev) => ({ ...prev, rowHeight: height }));
    updateVisibleWindow();
  }, [updateVisibleWindow]);

  const showPostMessageLoadMore = loadingMore;

  return (
    <div ref={rootRef} className="pb-28 px-3 sm:px-4">
      <AlbumGridErrorBoundary resetKeys={[]}>
      {topSpacer > 0 && (
        <div aria-hidden style={{ height: topSpacer, pointerEvents: 'none' }} />
      )}
      <div
        ref={gridRef}
        className="grid gap-4"
        style={{
          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
        }}
      >
        {visibleList.map((album, idx) => {
          const listIndex = startIndex + idx;
          const albumKey = getAlbumGridRowKey(album, listIndex);
          const overlayKey = album.album_key || album.id || albumKey;
          const st = cardStateRef.current.get(albumKey);
          const isSelected = selectedAlbumKey != null && (selectedAlbumKey === album.album_key || selectedAlbumKey === album.id);
          const isActiveAlbum = isNowPlaying(album, npKey);
          const isPlaying = isActiveAlbum;
          const isPlaybackActive = isActiveAlbum && playbackActive;
          const measureRef = idx === 0 ? handleMeasureCard : undefined;
          return (
            <div key={albumKey} ref={measureRef}>
              <AlbumCardErrorBoundary resetKey={albumKey}>
                <AlbumCard
                  albumKey={albumKey}
                  overlayKey={overlayKey}
                  album={album}
                  unavailable={st?.unavailable ?? false}
                  provisional={st?.provisional ?? false}
                  showUpdatingBadge={st?.showUpdatingBadge ?? false}
                  isReconnecting={st?.isReconnecting ?? false}
                  isSelected={isSelected}
                  isPlaying={isPlaying}
                  isPlaybackActive={isPlaybackActive}
                  versionCount={st?.versionCount ?? 0}
                  onClick={handleCardClick}
                  onDoubleClick={handleCardDoubleClick}
                  onEdit={handleCardEdit}
                  onContextMenu={handleCardContextMenu}
                  onInspect={handleCardInspect}
                  onReread={handleCardReread}
                  onRemove={handleCardRemove}
                  onVersionsClick={handleCardVersions}
                />
              </AlbumCardErrorBoundary>
            </div>
          );
        })}
      </div>
      {bottomSpacer > 0 && (
        <div aria-hidden style={{ height: bottomSpacer, pointerEvents: 'none' }} />
      )}
      {showPostMessageLoadMore && (
        <div
          className="grid gap-4"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}
        >
          {Array.from({ length: layoutMetrics.columns }, (_, idx) => (
            <AlbumCardSkeleton key={`load-more-skeleton-${idx}`} />
          ))}
        </div>
      )}
      {hasMore && (
        <div ref={sentinelRef} className="flex flex-col items-center justify-center gap-2 py-8">
          {loadingMore && !isIpcPostMessageFallbackActive() ? (
            <>
              <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
              <div className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                Loading more albums…
              </div>
            </>
          ) : !loadingMore ? (
            <div className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Scroll for more…</div>
          ) : null}
        </div>
      )}
      </AlbumGridErrorBoundary>
    </div>
  );
}

function AlbumCardSkeleton() {
  return (
    <div className="animate-pulse">
      <div
        className="aspect-square rounded-2xl"
        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}
      />
      <div className="mt-3 h-4 rounded" style={{ background: 'var(--bg-secondary)', width: '75%' }} />
      <div className="mt-2 h-3 rounded" style={{ background: 'var(--bg-secondary)', width: '50%' }} />
    </div>
  );
}

function PendingCoverPlaceholder({ title, artist }) {
  const color = hashColor(title || 'Album');
  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center p-3 text-center pointer-events-none"
      style={{
        background: `linear-gradient(145deg, ${color}dd 0%, ${color}88 55%, rgba(0,0,0,0.35) 100%)`,
      }}
    >
      <svg
        width="36"
        height="36"
        viewBox="0 0 24 24"
        fill="none"
        stroke="rgba(255,255,255,0.35)"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        style={{ marginBottom: 8 }}
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M9 10v4" />
        <path d="M15 10v4" />
        <path d="M9 12h6" />
      </svg>
      <div
        className="line-clamp-2"
        style={{
          fontSize: 11,
          fontWeight: 800,
          color: 'rgba(255,255,255,0.92)',
          lineHeight: 1.25,
          width: '100%',
        }}
      >
        {title}
      </div>
      <div
        className="truncate"
        style={{
          fontSize: 10,
          color: 'rgba(255,255,255,0.65)',
          marginTop: 4,
          width: '100%',
        }}
      >
        {artist}
      </div>
    </div>
  );
}

function AlbumCardPlaceholder() {
  return (
    <div className="w-full" aria-hidden>
      <div
        className="aspect-square rounded-2xl"
        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}
      />
      <div className="mt-3 h-4 rounded" style={{ background: 'var(--bg-secondary)', width: '70%' }} />
      <div className="mt-2 h-3 rounded" style={{ background: 'var(--bg-secondary)', width: '45%' }} />
    </div>
  );
}

const PILL = {
  background: 'rgba(0,0,0,0.75)',
  color: '#FFFFFF',
  fontSize: 11,
  fontWeight: 600,
  padding: '3px 8px',
  borderRadius: 4,
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
};
const PILL_WARN = {
  ...PILL,
  background: 'rgba(180,120,0,0.85)',
};

function albumCardPropsEqual(prev, next) {
  if (prev.albumKey !== next.albumKey) return false;
  if (prev.unavailable !== next.unavailable) return false;
  if (prev.provisional !== next.provisional) return false;
  if (prev.showUpdatingBadge !== next.showUpdatingBadge) return false;
  if (prev.isReconnecting !== next.isReconnecting) return false;
  if (prev.isSelected !== next.isSelected) return false;
  if (prev.isPlaying !== next.isPlaying) return false;
  if (prev.isPlaybackActive !== next.isPlaybackActive) return false;
  if (prev.versionCount !== next.versionCount) return false;
  if (prev.onClick !== next.onClick) return false;
  if (prev.onDoubleClick !== next.onDoubleClick) return false;
  if (prev.onEdit !== next.onEdit) return false;
  if (prev.onContextMenu !== next.onContextMenu) return false;
  if (prev.onInspect !== next.onInspect) return false;
  if (prev.onReread !== next.onReread) return false;
  if (prev.onRemove !== next.onRemove) return false;
  if (prev.onVersionsClick !== next.onVersionsClick) return false;
  const pa = prev.album;
  const na = next.album;
  if (pa === na) return true;
  return (
    (coerceDisplayText(pa?.title || pa?.album, '') === coerceDisplayText(na?.title || na?.album, ''))
    && (coerceDisplayText(pa?.artist_name || pa?.album_artist || pa?.artist, '') === coerceDisplayText(na?.artist_name || na?.album_artist || na?.artist, ''))
    && (pa?.cover_art_url || '') === (na?.cover_art_url || '')
    && (pa?.cover_art_thumb_hash || '') === (na?.cover_art_thumb_hash || '')
    && !!pa?.placeholder === !!na?.placeholder
    && !!pa?.provisional === !!na?.provisional
    && (pa?.embedded_cover_hash || '') === (na?.embedded_cover_hash || '')
    && (pa?.folder_cover_hash || '') === (na?.folder_cover_hash || '')
    && (pa?.external_cover_hash || '') === (na?.external_cover_hash || '')
    && (pa?.user_override_source || '') === (na?.user_override_source || '')
    && (pa?.cover_art_updated_at || pa?.updated_at || '') === (na?.cover_art_updated_at || na?.updated_at || '')
    && (pa?.enrichment_status || '') === (na?.enrichment_status || '')
    && !!pa?.is_duplicate === !!na?.is_duplicate
    && !!pa?.is_compilation === !!na?.is_compilation
    && (pa?.format || '') === (na?.format || '')
    && (pa?.bit_depth ?? '') === (na?.bit_depth ?? '')
    && (pa?.sample_rate ?? '') === (na?.sample_rate ?? '')
    && (pa?.dynamic_range ?? '') === (na?.dynamic_range ?? '')
    && (pa?.version_count ?? 0) === (na?.version_count ?? 0)
  );
}

const AlbumCard = memo(function AlbumCard({
  albumKey,
  overlayKey,
  album,
  onClick,
  onDoubleClick,
  onEdit,
  onContextMenu,
  onInspect,
  onReread,
  onRemove,
  onVersionsClick,
  unavailable,
  provisional,
  showUpdatingBadge,
  isReconnecting,
  isSelected,
  isPlaying,
  isPlaybackActive,
  versionCount,
}) {
  const overlay = useAlbumCoverOverlay(overlayKey || albumKey);
  const effectiveAlbum = useMemo(() => (
    overlay ? { ...album, ...overlay } : album
  ), [album, overlay]);

  const collectorMode = useCollectorMode();

  const status = effectiveAlbum?.enrichment_status || (effectiveAlbum?.enriched_at ? 'enriched' : 'pending');
  const showDot = status !== 'pending';
  const dotColor = {
    enriched: '#34d399',
    user_edited: '#34d399',
    needs_review: '#fbbf24',
    unidentified: '#fb7185',
    enriching: '#38bdf8',
  }[status] || null;

  const usePlaceholderArt = effectiveAlbum?.placeholder === true
    || (provisional && !effectiveAlbum?.cover_art_thumb_hash);
  const embHash = usePlaceholderArt ? null : normalizeCoverHash(effectiveAlbum?.embedded_cover_hash);
  const folHash = usePlaceholderArt ? null : normalizeCoverHash(effectiveAlbum?.folder_cover_hash);
  const extHash = usePlaceholderArt ? null : normalizeCoverHash(effectiveAlbum?.external_cover_hash);
  const thumbHash = usePlaceholderArt
    ? null
    : normalizeArtworkCacheKey(effectiveAlbum?.cover_art_thumb_hash);
  const bareAlbumKey = effectiveAlbum?.album_key || effectiveAlbum?.id || '';
  const isLocalLibraryAlbum =
    effectiveAlbum?.__source === 'local_library' || effectiveAlbum?.isLocalLibrary === true;
  const detailHash = usePlaceholderArt ? null : normalizeCoverHash(getExternalDetailHash(bareAlbumKey));
  const localCoverHash = embHash || folHash || extHash || thumbHash;
  const primaryEmbHash = embHash || detailHash;
  const resolvedEmbHash = primaryEmbHash;
  const resolvedFolHash = folHash;
  const albumFolderPath = usePlaceholderArt ? null : pickAlbumFolderPath(effectiveAlbum);
  const indexHashesEmpty = !embHash && !folHash && !extHash;
  const needsLazyHashResolve = !usePlaceholderArt
    && isLocalLibraryAlbum
    && indexHashesEmpty
    && shouldUseLocalLibraryIpc();
  const deferEnrichedCover = shouldDeferEnrichedCoverForLocalAlbum({
    isLocalLibraryAlbum,
    indexHashesEmpty,
    albumFolderPath,
    detailHashPending: needsLazyHashResolve && !isExternalCoverKnownArtless(bareAlbumKey),
  });
  const hasLocalArtSource = !usePlaceholderArt && !!(localCoverHash || albumFolderPath);
  const actionsDisabled = !!(unavailable || provisional);

  const enrichedCoverAllowed = useMemo(() => mayUseEnrichedCoverUrl({
    artworkSource: effectiveAlbum?.artwork_source || effectiveAlbum?.winning_source,
    embeddedCoverHash: embHash,
    folderCoverHash: folHash,
  }), [
    effectiveAlbum?.artwork_source,
    effectiveAlbum?.winning_source,
    embHash,
    folHash,
  ]);

  const [externalResolveRev, setExternalResolveRev] = useState(0);
  useEffect(() => {
    if (!bareAlbumKey) return undefined;
    return subscribeExternalCoverResolve(bareAlbumKey, () => {
      setExternalResolveRev((n) => n + 1);
    });
  }, [bareAlbumKey]);

  const isRemoteViewerShell = isRemoteCoverViewerShell();
  const isRemoteTunnel =
    isRemoteViewerShell
    || effectiveAlbum?.__source === 'remote_tunnel'
    || !!(effectiveAlbum?.cover_art_url && String(effectiveAlbum.cover_art_url).includes('/api/library/remote/cover/'));

  const onlineCover = useMemo(() => {
    if (!enrichedCoverAllowed) return null;
    const remote = pickRemoteTileCoverUrl(effectiveAlbum);
    if (!remote) return null;
    if (isRemoteTunnel && remote.includes('/api/library/remote/cover/')) return null;
    const coverBust = coerceDisplayText(
      effectiveAlbum?.cover_art_updated_at || effectiveAlbum?.updated_at || effectiveAlbum?.cover_art_source || null,
      '',
    );
    return coverBust
      ? `${remote}${remote.includes('?') ? '&' : '?'}v=${encodeURIComponent(coverBust)}`
      : remote;
  }, [
    enrichedCoverAllowed,
    effectiveAlbum,
    isRemoteTunnel,
  ]);

  const overrideSource = effectiveAlbum?.user_override_source;

  const initialIpc = isRemoteTunnel
    ? (getRemoteCoverFromCache(resolvedEmbHash, effectiveAlbum?.machine_id) ?? getRemoteCoverFromCache(resolvedFolHash, effectiveAlbum?.machine_id) ?? undefined)
    : ((hasResolvedCoverInCache(embHash) || hasResolvedCoverInCache(folHash) || hasResolvedCoverInCache(extHash))
      ? syncResolvedCoverUrl(primaryEmbHash, folHash, extHash)
      : undefined);
  const [localCoverUrl, setLocalCoverUrl] = useState(() => {
    if (usePlaceholderArt) return null;
    if (initialIpc !== undefined) return initialIpc || null;
    const syncThumb = syncLocalThumbDisplayUrl(effectiveAlbum);
    return syncThumb || null;
  });
  const [localResolutionComplete, setLocalResolutionComplete] = useState(
    usePlaceholderArt || (!hasLocalArtSource && !deferEnrichedCover),
  );
  const coverLoadedRef = useRef(false);
  const lastResolvedUrlRef = useRef(null);
  const prevBareAlbumKeyRef = useRef(bareAlbumKey);
  const [cacheRevision, setCacheRevision] = useState(0);
  const [coverFadeIn, setCoverFadeIn] = useState(false);
  const [primeGen, setPrimeGen] = useState(() => getViewportCoverPrimeGeneration());

  useEffect(() => subscribeViewportCoverPrime((gen, reason) => {
    // `null` = unsettled; `''` = settled miss; non-empty = settled hit.
    // Unsettled → always retry (cancel / stuck coverLoadedRef).
    // Settled miss → retry only on retained-wall restore (thumbs may be ready now).
    const wallRestore = reason === 'album-wall-visible';
    if (lastResolvedUrlRef.current != null) {
      if (!(wallRestore && lastResolvedUrlRef.current === '')) return;
      lastResolvedUrlRef.current = null;
    }
    coverLoadedRef.current = false;
    setPrimeGen(gen);
  }), []);

  useEffect(() => {
    const albumChanged = bareAlbumKey !== prevBareAlbumKeyRef.current;
    if (albumChanged) prevBareAlbumKeyRef.current = bareAlbumKey;
    const cachedUrl = syncResolvedCoverUrl(primaryEmbHash, folHash, extHash);
    if (cachedUrl) {
      lastResolvedUrlRef.current = cachedUrl;
      coverLoadedRef.current = true;
      setLocalCoverUrl(cachedUrl);
      setLocalResolutionComplete(true);
      return;
    }
    if (!albumChanged) return;
    lastResolvedUrlRef.current = null;
    coverLoadedRef.current = false;
    setLocalResolutionComplete(!hasLocalArtSource && !deferEnrichedCover);
    setCoverFadeIn(false);
  }, [bareAlbumKey, hasLocalArtSource, deferEnrichedCover, primaryEmbHash, folHash, extHash]);

  useEffect(() => {
    if (!embHash && !folHash && !extHash) return undefined;
    return subscribeCoverCacheLoaded((hash) => {
      if (hash === embHash || hash === folHash || hash === extHash) {
        setCacheRevision((n) => n + 1);
      }
    });
  }, [embHash, folHash, extHash]);

  useEffect(() => {
    if (!needsLazyHashResolve || !bareAlbumKey) return;
    if (primaryEmbHash || folHash || extHash) return;
    if (isExternalCoverKnownArtless(bareAlbumKey)) return;
    requestExternalAlbumCoverHash(bareAlbumKey);
  }, [needsLazyHashResolve, bareAlbumKey, primaryEmbHash, folHash, extHash, externalResolveRev]);

  useEffect(() => {
    if (isRemoteTunnel) {
      const hash = resolvedEmbHash || resolvedFolHash;
      if (!hash) {
        setLocalCoverUrl(null);
        setLocalResolutionComplete(true);
        return;
      }
      const cachedRemote = getRemoteCoverFromCache(hash, effectiveAlbum?.machine_id)
        ?? getRemoteCoverFromCache(hash, null);
      if (cachedRemote !== undefined) {
        setLocalCoverUrl(cachedRemote || null);
        coverLoadedRef.current = true;
        setLocalResolutionComplete(true);
        return;
      }
      if (coverLoadedRef.current) return;
      let cancelled = false;
      coverLoadedRef.current = true;
      (async () => {
        const url = await getRemoteCoverArt(hash, effectiveAlbum?.machine_id || null);
        if (!cancelled) {
          lastResolvedUrlRef.current = url || '';
          setLocalCoverUrl(url);
          setLocalResolutionComplete(true);
        } else {
          coverLoadedRef.current = false;
        }
      })();
      return () => {
        cancelled = true;
        if (lastResolvedUrlRef.current == null) coverLoadedRef.current = false;
      };
    }

    const cachedUrl = syncResolvedCoverUrl(primaryEmbHash, folHash, extHash);
    if (cachedUrl) {
      setLocalCoverUrl(cachedUrl);
      lastResolvedUrlRef.current = cachedUrl;
      coverLoadedRef.current = true;
      setLocalResolutionComplete(true);
      return;
    }
    if (coverLoadedRef.current) return;

    if (!primaryEmbHash && !folHash && !extHash && !thumbHash) {
      if (needsLazyHashResolve && !isExternalCoverKnownArtless(bareAlbumKey)) {
        return;
      }
      lastResolvedUrlRef.current = '';
      setLocalCoverUrl(null);
      setLocalResolutionComplete(true);
      return;
    }

    let cancelled = false;
    coverLoadedRef.current = true;
    (async () => {
      let url = null;
      try {
        if (thumbHash) {
          url = await fetchValidatedLocalThumbUrl(thumbHash);
        }
        if (!url) {
          url = await resolveGridHashCoverInOrder(primaryEmbHash, folHash, extHash, effectiveAlbum);
        }
      } finally {
        if (!cancelled) {
          lastResolvedUrlRef.current = url || '';
          setLocalCoverUrl(url);
          setLocalResolutionComplete(true);
        } else {
          coverLoadedRef.current = false;
        }
      }
    })();
    return () => {
      cancelled = true;
      if (lastResolvedUrlRef.current == null) coverLoadedRef.current = false;
    };
  }, [
    primaryEmbHash,
    folHash,
    extHash,
    thumbHash,
    detailHash,
    embHash,
    albumKey,
    bareAlbumKey,
    effectiveAlbum,
    isRemoteTunnel,
    cacheRevision,
    needsLazyHashResolve,
    externalResolveRev,
    primeGen,
  ]);

  const coverUrl = useMemo(() => pickLocalLibraryDisplayCover({
    localCoverUrl,
    onlineCoverUrl: onlineCover,
    overrideSource,
    hasLocalArtSource: hasLocalArtSource || deferEnrichedCover,
    localResolutionComplete,
    isRemoteTunnel,
    gridPreferPlaceholderOverEnriched: true,
  }), [
    overrideSource,
    localCoverUrl,
    onlineCover,
    isRemoteTunnel,
    hasLocalArtSource,
    deferEnrichedCover,
    localResolutionComplete,
  ]);

  const title = coerceDisplayText(effectiveAlbum?.title || effectiveAlbum?.album, 'Untitled').trim() || 'Untitled';
  const artist = coerceDisplayText(
    effectiveAlbum?.artist_name || effectiveAlbum?.album_artist || effectiveAlbum?.artist,
    'Unknown Artist',
  ).trim() || 'Unknown Artist';

  const formatText = coerceDisplayText(getFormatBadge(effectiveAlbum), '');
  const sourceText = coerceDisplayText(getSourceBadge(effectiveAlbum), '');
  const dsd = isDsd(effectiveAlbum);
  const hires = isHiRes(effectiveAlbum);
  const drRaw = effectiveAlbum?.dynamic_range;
  const drValue = drRaw != null && drRaw !== ''
    ? `DR${coerceDisplayText(drRaw, '')}`.replace(/^DR$/, '')
    : null;

  const [imgErrorKey, setImgErrorKey] = useState(0);
  const [displayUrlOverride, setDisplayUrlOverride] = useState(null);
  const coverImgRef = useRef(null);
  const triedOnlineRef = useRef(false);
  const ipcFallbackAttemptedRef = useRef(false);
  const prevResolvedUrlRef = useRef(null);
  const prevCoverRef = useRef(coverUrl);
  useEffect(() => {
    if (prevCoverRef.current === coverUrl) return;
    prevCoverRef.current = coverUrl;
    triedOnlineRef.current = false;
    ipcFallbackAttemptedRef.current = false;
    setImgErrorKey(0);
    setDisplayUrlOverride(null);
  }, [coverUrl]);

  let activeUrl = displayUrlOverride ?? withCoverThumbParam(coverUrl);
  if (imgErrorKey > 0 && triedOnlineRef.current) {
    activeUrl = null;
  } else if (imgErrorKey > 0 && !triedOnlineRef.current && enrichedCoverAllowed && onlineCover && coverUrl !== onlineCover) {
    activeUrl = withCoverThumbParam(onlineCover);
  }

  const resolvedDisplayUrl = activeUrl && imgErrorKey < 2 ? activeUrl : null;

  useEffect(() => {
    if (!resolvedDisplayUrl) return;
    lastResolvedUrlRef.current = resolvedDisplayUrl;
    const prev = prevResolvedUrlRef.current;
    if (prev && prev !== resolvedDisplayUrl) {
      setCoverFadeIn(true);
      let raf2 = 0;
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setCoverFadeIn(false));
      });
      prevResolvedUrlRef.current = resolvedDisplayUrl;
      return () => {
        cancelAnimationFrame(raf1);
        if (raf2) cancelAnimationFrame(raf2);
      };
    }
    prevResolvedUrlRef.current = resolvedDisplayUrl;
  }, [resolvedDisplayUrl]);

  const shownUrl = usePlaceholderArt ? null : (resolvedDisplayUrl || lastResolvedUrlRef.current);
  const showCover = !usePlaceholderArt && Boolean(
    shownUrl
      && imgErrorKey < 2
      && (!!localCoverUrl || !isMissingArtwork(effectiveAlbum)),
  );
  const hasResolvedCover = showCover || !!lastResolvedUrlRef.current;

  useEffect(() => () => {
    const img = coverImgRef.current;
    if (img) {
      img.removeAttribute('src');
      img.src = '';
    }
    lastResolvedUrlRef.current = null;
    coverLoadedRef.current = false;
  }, []);

  const handleImgError = useCallback(() => {
    invalidateTileCoverArtOnLoadError(effectiveAlbum);
    if (isRemoteTunnel) {
      setImgErrorKey(2);
      return;
    }
    const failedUrl = displayUrlOverride ?? withCoverThumbParam(coverUrl);
    if (!displayUrlOverride && isCoverThumbRequest(failedUrl)) {
      const fullUrl = withoutCoverThumbParam(failedUrl);
      if (fullUrl && fullUrl !== failedUrl) {
        logCoverThumb404(failedUrl);
        setDisplayUrlOverride(fullUrl);
        return;
      }
    }
    if (
      !ipcFallbackAttemptedRef.current
      && isCoverIPCAvailable()
      && (primaryEmbHash || folHash || extHash)
      && (isCoverProxyUrl(coverUrl) || isCoverProxyUrl(localCoverUrl))
    ) {
      ipcFallbackAttemptedRef.current = true;
      coverLoadedRef.current = false;
      (async () => {
        const url = await resolveGridHashCoverInOrder(primaryEmbHash, folHash, extHash, effectiveAlbum);
        if (url) {
          setLocalCoverUrl(url);
          setDisplayUrlOverride(null);
          setImgErrorKey(0);
          return;
        }
        if (!triedOnlineRef.current && enrichedCoverAllowed && onlineCover && coverUrl !== onlineCover) {
          triedOnlineRef.current = true;
          setDisplayUrlOverride(null);
          setImgErrorKey((k) => k + 1);
          return;
        }
        setImgErrorKey(2);
      })();
      return;
    }
    if (!triedOnlineRef.current && enrichedCoverAllowed && onlineCover && coverUrl !== onlineCover) {
      triedOnlineRef.current = true;
      setDisplayUrlOverride(null);
    }
    setImgErrorKey((k) => k + 1);
  }, [onlineCover, coverUrl, localCoverUrl, displayUrlOverride, isRemoteTunnel, primaryEmbHash, folHash, extHash, enrichedCoverAllowed, effectiveAlbum]);

  const hoverClass = collectorMode
    ? 'opacity-100'
    : 'opacity-0 pointer-fine:group-hover:opacity-100';

  const handlePlayAlbum = useCallback(() => {
    if (provisional) {
      notifyProvisionalCatalogActionBlocked();
      return;
    }
    playAlbumFromTile(effectiveAlbum);
  }, [effectiveAlbum, provisional]);

  if (COVER_TILE_RENDER_MODE === 'tile-appears-with-art' && !hasResolvedCover) {
    return null;
  }

  return (
    // Outer wrapper is a div+role=button (not a real <button>) because the
    // "Show Inspector" overlay below is itself a <button>, and a <button>
    // cannot legally contain another <button> per the HTML spec — React DEV
    // logs a validateDOMNesting warning and produces a hydration mismatch.
    // We re-add the keyboard activation that a native button would supply.
    <div
      role="button"
      tabIndex={actionsDisabled ? -1 : 0}
      aria-disabled={actionsDisabled || undefined}
      data-provisional={provisional ? 'true' : undefined}
      data-updating={showUpdatingBadge ? 'true' : undefined}
      onClick={() => onClick?.(albumKey)}
      onDoubleClick={() => onDoubleClick?.(albumKey)}
      onContextMenu={(e) => onContextMenu?.(albumKey, e)}
      onKeyDown={(e) => {
        if (actionsDisabled) {
          if (provisional && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            notifyProvisionalCatalogActionBlocked();
          }
          return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.(albumKey);
        }
      }}
      className={`transition-all duration-200 group w-full text-left p-0 border-0 bg-transparent font-inherit touch-manipulation select-none [-webkit-tap-highlight-color:transparent] ${actionsDisabled ? 'cursor-default' : 'active:scale-[0.97] cursor-pointer'}`}
    >
      <div
        className="relative aspect-square rounded-2xl overflow-hidden"
        style={{
          background: 'var(--bg-secondary)',
          border: (isPlaying || isSelected)
            ? '2px solid var(--accent-primary, #C9A84C)'
            : '1px solid var(--border-light)',
          boxShadow: isPlaying
            ? '0 0 12px rgba(201,168,76,0.3)'
            : isSelected ? '0 4px 20px rgba(201,168,76,0.25)' : 'var(--shadow-card)',
        }}
      >
        {showCover ? (
          <img
            ref={coverImgRef}
            src={shownUrl}
            alt=""
            className="w-full h-full object-cover pointer-fine:group-hover:scale-[1.04] transition-transform duration-300 pointer-events-none"
            style={{
              opacity: coverFadeIn ? 0 : 1,
              transition: 'opacity 150ms ease-out',
            }}
            loading="lazy"
            draggable={false}
            onError={handleImgError}
          />
        ) : (
          <PendingCoverPlaceholder title={title} artist={artist} />
        )}

        {!actionsDisabled && (
          <AlbumPlayButton
            onPlay={handlePlayAlbum}
            isActive={isPlaying}
            isPlaying={isPlaybackActive}
          />
        )}

        {/* Top-right: ⋯ overflow, version badge, inspect */}
        <div className="absolute top-2 right-2 flex items-center gap-1.5" style={{ zIndex: 2 }}>
          {isLocalLibraryAlbum && onReread && onRemove && (
            <AlbumTileOverflowMenu
              album={effectiveAlbum}
              disabled={actionsDisabled}
              onReread={() => onReread(albumKey)}
              onRemove={() => onRemove(albumKey)}
            />
          )}
          {versionCount > 1 && (
            <button
              type="button"
              className="flex items-center justify-center"
              style={{
                width: 20, height: 20, borderRadius: 10,
                background: 'var(--accent-primary, #C9A84C)',
                color: '#000', fontSize: 10, fontWeight: 800,
                boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
              }}
              title={`${versionCount} versions in library — tap to view`}
              onClick={(e) => onVersionsClick?.(albumKey, e)}
            >
              {versionCount}
            </button>
          )}
          {onInspect && (
            <button
              type="button"
              className={`flex items-center justify-center transition-opacity duration-150 ${collectorMode ? 'opacity-100' : 'opacity-0 pointer-fine:group-hover:opacity-100 pointer-coarse:opacity-100'}`}
              style={{
                width: 28, height: 28, borderRadius: 14,
                background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
                border: 'none', cursor: 'pointer', padding: 0,
                boxShadow: '0 1px 6px rgba(0,0,0,0.4)',
              }}
              title="Show Inspector"
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); onInspect?.(albumKey); }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
            </button>
          )}
        </div>

        {/* Now-playing equalizer indicator */}
        {isPlaying && (
          <div
            className="absolute top-2 left-2 flex items-end gap-[2px] pointer-events-none"
            style={{ zIndex: 3, height: 14 }}
          >
            <span className="inline-block w-[3px] rounded-sm" style={{ background: '#C9A84C', animation: 'eqBar1 0.8s ease-in-out infinite' }} />
            <span className="inline-block w-[3px] rounded-sm" style={{ background: '#C9A84C', animation: 'eqBar2 0.6s ease-in-out infinite 0.15s' }} />
            <span className="inline-block w-[3px] rounded-sm" style={{ background: '#C9A84C', animation: 'eqBar3 0.75s ease-in-out infinite 0.3s' }} />
          </div>
        )}

        {/* Updating… (boot snapshot) / Reconnecting / Offline ribbons */}
        {(showUpdatingBadge || unavailable || isReconnecting) && (
          <div
            className="absolute top-2 left-2 px-2 py-0.5 rounded pointer-events-none"
            data-testid={showUpdatingBadge && !unavailable && !isReconnecting ? 'catalog-updating-badge' : undefined}
            style={{
              ...PILL,
              background: showUpdatingBadge && !unavailable && !isReconnecting
                ? 'rgba(201,168,76,0.72)'
                : (isReconnecting ? 'rgba(201,168,76,0.92)' : 'rgba(239,68,68,0.92)'),
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: '0.04em',
              textTransform: showUpdatingBadge && !unavailable && !isReconnecting ? 'none' : 'uppercase',
            }}
          >
            {showUpdatingBadge && !unavailable && !isReconnecting
              ? 'Updating…'
              : (isReconnecting ? 'Reconnecting' : 'Offline')}
          </div>
        )}

        {/* Badge row — bottom of artwork, 8px inset */}
        <div
          className="absolute bottom-2 left-2 right-2 flex flex-wrap items-end gap-1 pointer-events-none"
        >
          {/* Format badge — always visible */}
          {formatText && (
            <span className="flex items-center gap-1" style={PILL}>
              {hires && !dsd && (
                <HiResAudioBadge bitDepth={effectiveAlbum?.bit_depth} sampleRate={effectiveAlbum?.sample_rate} height={13} />
              )}
              {formatText}
            </span>
          )}

          {/* Hover / Collector-mode badges */}
          {sourceText && (
            <span className={`transition-opacity duration-150 ${hoverClass}`} style={PILL}>
              {sourceText}
            </span>
          )}
          {drValue && (
            <span className={`transition-opacity duration-150 ${hoverClass}`} style={PILL}>
              {drValue}
            </span>
          )}
          {effectiveAlbum?.is_duplicate && (
            <span className={`transition-opacity duration-150 ${hoverClass}`} style={PILL_WARN}>
              Dup
            </span>
          )}
        </div>
      </div>

      <div className="mt-3">
        <div className="font-extrabold truncate text-sm sm:text-[15px]" style={{ color: 'var(--text-primary)' }}>{title}</div>
        <div className="truncate text-xs sm:text-sm" style={{ color: 'var(--text-secondary)' }}>{artist}</div>
        {effectiveAlbum?.is_compilation && (
          <div className="flex gap-1.5 mt-1">
            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold text-purple-300/70 bg-purple-500/10 border border-purple-400/15">
              Mix
            </span>
          </div>
        )}
      </div>
    </div>
  );
}, albumCardPropsEqual);
